import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export const GmailOAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing Gmail connection...');
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const gmailParam = urlParams.get('gmail');
        const gmailEmail = urlParams.get('email');
        const errorParam = urlParams.get('error');

        if (gmailParam === 'connected') {
          const warningParam = urlParams.get('warning');
          setStatus('success');
          setMessage(
            warningParam === 'read_scope_missing'
              ? 'Gmail connected for sending only — email sync needs read access. Reconnect and allow "View your email messages".'
              : 'Gmail account connected successfully!'
          );
          setEmail(gmailEmail);

          // Send success message to parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'GMAIL_OAUTH_SUCCESS',
              email: gmailEmail,
              warning: warningParam
            }, window.location.origin);
          }
          
          // Auto-close after 2 seconds
          setTimeout(() => {
            window.close();
          }, 2000);
          
        } else if (gmailParam === 'error') {
          setStatus('error');
          const errorMessage = errorParam ? decodeURIComponent(errorParam) : 'Failed to connect Gmail account';
          setMessage(errorMessage);
          
          // Send error message to parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'GMAIL_OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin);
          }
          
          // Auto-close after 3 seconds
          setTimeout(() => {
            window.close();
          }, 3000);
          
        } else {
          // Still processing or unexpected state
          setMessage('Completing Gmail connection...');
          
          // Timeout after 30 seconds
          setTimeout(() => {
            setStatus('error');
            setMessage('Connection timed out. Please try again.');
            if (window.opener) {
              window.opener.postMessage({
                type: 'GMAIL_OAUTH_ERROR',
                error: 'Connection timed out'
              }, window.location.origin);
            }
            setTimeout(() => window.close(), 2000);
          }, 30000);
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred');
        
        if (window.opener) {
          window.opener.postMessage({
            type: 'GMAIL_OAUTH_ERROR',
            error: 'Unexpected error during OAuth callback'
          }, window.location.origin);
        }
        
        setTimeout(() => window.close(), 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          {status === 'loading' && (
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          )}
          {status === 'error' && (
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          )}
        </div>
        
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          {status === 'loading' && 'Connecting Gmail...'}
          {status === 'success' && 'Success!'}
          {status === 'error' && 'Connection Failed'}
        </h2>
        
        <p className="text-slate-600 mb-4">{message}</p>
        
        {email && (
          <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
            Connected: {email}
          </p>
        )}
        
        {status !== 'loading' && (
          <p className="text-xs text-slate-400 mt-4">
            This window will close automatically...
          </p>
        )}
        
        <button
          id="oauth-close-window-btn"
          onClick={() => window.close()}
          className="mt-4 px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
        >
          Close Window
        </button>
      </div>
    </div>
  );
};